import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button } from './ui-kit';
import MultiSelectFilter from './MultiSelectFilter';
import usePermissionFilterOptions from '../hooks/usePermissionFilterOptions';

/**
 * Reusable filter panel for Service / Resource / Action multi-selects.
 * Optionally renders a Status segmented control and an `extraFilters` slot.
 *
 * value: { services: string[], resources: string[], actions: string[], status?: string }
 * onChange: (next) => void
 */
function PermissionFiltersPanel({
  title = 'Filter',
  value,
  onChange,
  statusOptions,
  extraFilters,
  isExpanded = false,
  onToggleExpand = () => {},
  onClearAll,
}) {
  const services = value?.services || [];
  const resources = value?.resources || [];
  const actions = value?.actions || [];
  const status = value?.status;

  const {
    services: serviceOptions,
    resources: resourceOptions,
    actions: actionOptions,
    isServicesLoading,
    isResourcesLoading,
    isActionsLoading,
  } = usePermissionFilterOptions({
    selectedServices: services,
    selectedResources: resources,
    enabled: isExpanded,
  });

  const update = useCallback(
    (patch) => onChange({ ...value, ...patch }),
    [onChange, value]
  );

  const handleServicesChange = useCallback((next) => update({ services: next }), [update]);
  const handleResourcesChange = useCallback((next) => update({ resources: next }), [update]);
  const handleActionsChange = useCallback((next) => update({ actions: next }), [update]);
  const handleStatusChange = useCallback((next) => update({ status: next }), [update]);

  const handleClearAll = useCallback(() => {
    if (onClearAll) {
      onClearAll();
    } else {
      onChange({
        ...value,
        services: [],
        resources: [],
        actions: [],
        ...(statusOptions ? { status: statusOptions[0]?.value ?? 'all' } : {}),
      });
    }
  }, [onClearAll, onChange, value, statusOptions]);

  return (
    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
      <Box
        sx={{
          mt: 2,
          p: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, mb: 1 }}>
          {title}
        </Typography>

        {(extraFilters || statusOptions) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {statusOptions && (
              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {statusOptions.map((option) => (
                    <Button
                      key={option.value}
                      variant={status === option.value ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleStatusChange(option.value)}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {extraFilters}
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          <MultiSelectFilter
            label="Services"
            options={serviceOptions}
            value={services}
            onChange={handleServicesChange}
            loading={isServicesLoading}
            placeholder="Select services"
          />
          <MultiSelectFilter
            label="Resources"
            options={resourceOptions}
            value={resources}
            onChange={handleResourcesChange}
            loading={isResourcesLoading}
            placeholder="Select resources"
          />
          <MultiSelectFilter
            label="Actions"
            options={actionOptions}
            value={actions}
            onChange={handleActionsChange}
            loading={isActionsLoading}
            placeholder="Select actions"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            startIcon={<RestartAltIcon />}
            onClick={handleClearAll}
            variant="neutral"
            size="small"
          >
            Clear All Filters
          </Button>
          <Button onClick={onToggleExpand} variant="neutral" size="small">
            Close
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
}

export default React.memo(PermissionFiltersPanel);
